import { legalConfig } from '../../config/customerConfig';

/**
 * Public-facing data security statement, shared with customers doing vendor
 * due diligence. Written at the level of controls and commitments (not internal
 * architecture), and fully brand-parameterized from {@link legalConfig}: every
 * name/domain/email resolves per subsidiary build, so the page never references
 * a sibling brand or any partisan affiliation.
 */
const SecurityStatement = () => {
  const { legalEntityName, websiteDomain } = legalConfig;
  // Display brand without the legal suffix (e.g. "VoteCivic", "SignalVote").
  const brand = legalEntityName.replace(/,?\s*LLC\.?$/i, '').trim();
  const securityEmail = `adil@${websiteDomain}`;

  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <h1 className="mb-2 text-3xl font-bold">Data Security Statement</h1>
      <p className="mb-8 text-sm text-gray-500">
        <strong>Last Updated: August 7, 2026</strong>
      </p>

      <p className="mb-8">
        {legalEntityName} ("we," "us," or "our") provides a hosted platform for political
        committees to collect and manage contributions. We take the security of the data we
        process on our customers' behalf seriously. This statement summarizes the safeguards we
        apply to donor and committee data.
      </p>

      <Section title="Payment Security">
        <p>
          All card payments are processed by <strong>Stripe</strong>, a PCI DSS Level 1 certified
          payment provider. Card details are entered directly into Stripe's secure, hosted payment
          fields and are <strong>never transmitted through or stored on {brand}'s systems</strong>.
          Because we never handle raw card data, our environment remains within{' '}
          <strong>PCI DSS SAQ-A</strong> scope — the lowest-risk self-assessment category.
        </p>
      </Section>

      <Section title="Encryption">
        <p>
          All data is encrypted <strong>in transit</strong> (TLS 1.2 or higher) and{' '}
          <strong>at rest</strong> using industry-standard encryption.
        </p>
      </Section>

      <Section title="Infrastructure and Hosting">
        <p>
          The platform is hosted on <strong>Google Cloud Platform</strong>, whose underlying
          infrastructure maintains SOC 2, ISO 27001, and PCI DSS certifications. We rely on managed,
          access-controlled cloud services for data storage and for the management of application
          secrets.
        </p>
      </Section>

      <Section title="Access Controls">
        <p>
          Access to production systems and customer data is restricted on a least-privilege basis,
          limited to authorized personnel, and protected by strong authentication.
        </p>
      </Section>

      <Section title="Tenant Isolation">
        <p>
          Each committee's data is logically segregated so that one customer's data is not
          accessible to another.
        </p>
      </Section>

      <Section title="Sub-processors">
        <p>
          We use a limited set of vendors to deliver the service: <strong>Stripe</strong> (payment
          processing), <strong>Google Cloud Platform</strong> (hosting and storage), and{' '}
          <strong>Resend</strong> (transactional email, such as donation receipts). We also use a
          limited set of infrastructure and monitoring providers. A current sub-processor list is
          available on request.
        </p>
      </Section>

      <Section title="Data Handling and Retention">
        <p className="mb-4">
          We process donor information (such as name, contact details, and contribution details)
          solely to operate the donation service and to help our customers meet their
          campaign-finance obligations. Certain contribution data is disclosed to federal and state
          regulators <strong>as required by campaign-finance law</strong>.
        </p>
        <p>
          We retain personal information only as long as reasonably necessary for the purpose for
          which it was collected, unless a longer retention period is required or permitted by law,
          and we delete or return it on request subject to those legal recordkeeping requirements.
        </p>
      </Section>

      <Section title="Incident Response">
        <p>
          We maintain an incident-response process. In the event of a confirmed data breach
          affecting a customer's data, we will notify the affected customer without undue delay and{' '}
          <strong>within 72 hours</strong> of confirming the breach.
        </p>
      </Section>

      <Section title="Privacy">
        <p>
          We handle personal data in accordance with applicable privacy laws. For details on the
          information we collect and how it is used, see our{' '}
          <a href="/privacy-policy" className="text-blue-600 underline hover:text-blue-800">
            Privacy Policy
          </a>
          . A Data Processing Addendum (DPA) is available on request.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For questions about this statement or our security practices, contact us at{' '}
          <a
            href={`mailto:${securityEmail}`}
            className="text-blue-600 underline hover:text-blue-800"
          >
            {securityEmail}
          </a>
          .
        </p>
      </Section>

      <div className="mt-8 border-t border-gray-200 pt-6 text-sm text-gray-500">
        <a href="/privacy-policy" className="text-blue-600 underline hover:text-blue-800">
          Privacy Policy
        </a>
        {' | '}
        <a href="/" className="text-blue-600 underline hover:text-blue-800">
          Home
        </a>
      </div>
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export default SecurityStatement;
